/**
 * Normalizes timestamp to milliseconds format
 * @param timestamp - Unix timestamp in seconds or milliseconds
 * @returns timestamp in milliseconds
 */
function normalizeTimestamp(timestamp: number): number {
    // If timestamp has 10 digits or less, it's likely in seconds
    // Convert to milliseconds by multiplying by 1000
    if (timestamp < 10000000000) { // 10^10 (10 digits max for seconds format)
        return timestamp * 1000;
    }
    // Already in milliseconds format
    return timestamp;
}

/**
 * Validates if a timestamp is valid for formatting
 * @param timestamp - Unix timestamp to validate (seconds or milliseconds)
 * @returns true if timestamp is valid, false otherwise
 */
function isValidTimestamp(timestamp: any): timestamp is number {
    // Keep essential undefined check for original compatibility
    if (timestamp === undefined) {
        return false;
    }
    
    // Check for zero (Unix epoch) which indicates missing/null data and causes 1970 dates
    if (timestamp === 0) {
        return false;
    }
    
    // Check for negative timestamps
    if (timestamp < 0) {
        return false;
    }
    
    // Allow all other values through, including null and non-numeric values
    // This preserves compatibility with existing forecast and appointment processing
    return true;
}

function convertUnix(unixTimestamp: number, timeZone: string, locale="en-gb") {
    if (!isValidTimestamp(unixTimestamp)) {
        return ""
    }
    
    // Normalize timestamp to milliseconds
    const normalizedTimestamp = normalizeTimestamp(unixTimestamp);
    
    if (locale === "en-gb") {
        return Intl.DateTimeFormat("en-gb", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: timeZone
        }).format(normalizedTimestamp)
    } else {
       return Intl.DateTimeFormat(locale, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: timeZone
        }).format(normalizedTimestamp)
    }
};

function convertUnixDate(unixTimestamp: number, timeZone: string) {
    if (!isValidTimestamp(unixTimestamp)) {
        return ""
    }
    
    // Normalize timestamp to milliseconds
    const normalizedTimestamp = normalizeTimestamp(unixTimestamp);
    
    return Intl.DateTimeFormat("en-gb", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      timeZone: timeZone
  }).format(normalizedTimestamp)
};

function convertUnixDateNumeric(unixTimestamp: number, timeZone: string) {
    if (!isValidTimestamp(unixTimestamp)) {
        return ""
    }
    
    // Normalize timestamp to milliseconds
    const normalizedTimestamp = normalizeTimestamp(unixTimestamp);
    
    return parseInt(Intl.DateTimeFormat("sv-SE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: timeZone
    }).format(normalizedTimestamp).split("-").join(""))
};

function convertUnixToShiftDate(unixTimestamp: number, timeZone: string) {
    if (!isValidTimestamp(unixTimestamp)) {
        return ""
    }
    
    // Normalize timestamp to milliseconds
    const normalizedTimestamp = normalizeTimestamp(unixTimestamp);
    
    const hour = parseInt(Intl.DateTimeFormat("sv-SE", {
      hour: "2-digit",
      hour12: false,
      timeZone: timeZone
    }).format(normalizedTimestamp))

    const daysBack = hour < 6 ? 1 : 0;

    const shiftDate = Intl.DateTimeFormat("sv-SE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: timeZone
    }).format(normalizedTimestamp - (daysBack * 24 * 60 * 60 * 1000))
    return shiftDate;
  };

function convertUnixToLocalHour(unixTimestamp: number, timeZone: string): number {
    if (!isValidTimestamp(unixTimestamp)) {
        return -1; // Return -1 to indicate invalid timestamp
    }
    
    // Normalize timestamp to milliseconds
    const normalizedTimestamp = normalizeTimestamp(unixTimestamp);
    
    return parseInt(Intl.DateTimeFormat("sv-SE", {
        hour: "2-digit",
        hour12: false,
        timeZone: timeZone
    }).format(normalizedTimestamp))
}


export { convertUnix, convertUnixDate, convertUnixToShiftDate, convertUnixDateNumeric, convertUnixToLocalHour };
